import { useEffect } from 'react';
import { useBookingStore } from '../store/useBookingStore';
import { useFieldValues } from './useFieldValues';
import { useConditionalLogic } from './useConditionalLogic';

interface UseDOMFieldSyncOptions {
	formId: number;
	bookingFieldId: number;
	serviceFieldId: number | null;
	fallbackServiceId: number;
	resourceFieldIds: number[];
}

/**
 * Internal hook to sync service field value to store
 */
function useServiceFieldSync(
	formId: number,
	serviceFieldId: number | null,
	fallbackServiceId: number
) {
	const setCurrentServiceId = useBookingStore(
		(state) => state.setCurrentServiceId
	);

	// Watch service field (manual mode)
	const dynamicServiceId = useFieldValues({
		formId,
		fieldIds: serviceFieldId ? [serviceFieldId] : [],
		shouldFetch: serviceFieldId !== null,
		transform: (value) => parseInt(value, 10),
		filter: (value) => value > 0,
	})[0];

	// Sync service ID to store
	useEffect(() => {
		const serviceId = dynamicServiceId || fallbackServiceId;
		setCurrentServiceId(serviceId);
	}, [dynamicServiceId, fallbackServiceId, setCurrentServiceId]);
}

/**
 * Internal hook to sync resource field values to store
 */
function useResourceFieldSync(formId: number, resourceFieldIds: number[]) {
	const setSelectedResourceIds = useBookingStore(
		(state) => state.setSelectedResourceIds
	);
	const setResourceMode = useBookingStore((state) => state.setResourceMode);

	// Track resource field visibility
	const fieldVisibility = useConditionalLogic({
		formId,
		fieldIds: resourceFieldIds,
	});

	// Watch resource fields. No `filter` here: the values are read back by
	// field position below, so every field has to keep its slot even when it
	// holds no selectable value (an automatic field's own input is empty).
	const manualResources = useFieldValues({
		formId,
		fieldIds: resourceFieldIds,
		shouldFetch: formId !== 0,
		transform: (value) => parseInt(value, 10) || 0,
	});

	// Sync resources to store
	useEffect(() => {
		if (formId === 0) {
			return;
		}

		const newResources: number[] = [];
		let detectedMode: 'any' | 'all' = 'all';

		resourceFieldIds.forEach((fieldId, index) => {
			// Skip conditionally hidden fields
			if (fieldVisibility[fieldId] === false) {
				return;
			}

			// Check allocation mode
			if (detectedMode !== 'any') {
				const container = document.querySelector(
					`#field_${formId}_${fieldId} .ginput_container_resource`
				) as HTMLElement;
				const allocationMode = container?.dataset?.allocationMode;

				if (allocationMode === 'automatic') {
					detectedMode = 'any';
				}
			}

			// Check for automatic/static allocation resources
			const resourcesInput = document.querySelector(
				`#input_${formId}_${fieldId}_resources`
			) as HTMLInputElement;

			if (resourcesInput?.value) {
				try {
					const resourceIds = JSON.parse(resourcesInput.value);
					if (Array.isArray(resourceIds) && resourceIds.length > 0) {
						newResources.push(...resourceIds);
						return;
					}
				} catch (e) {
					// Invalid JSON, fall through
				}
			}

			// Fall back to manual mode
			const resourceId = manualResources[index];
			if (resourceId > 0) {
				newResources.push(resourceId);
			}
		});

		setSelectedResourceIds(newResources);
		setResourceMode(detectedMode);
	}, [
		formId,
		resourceFieldIds,
		fieldVisibility,
		manualResources,
		setSelectedResourceIds,
		setResourceMode,
	]);
}

/**
 * Helper to extract field ID from a field element
 */
function extractFieldId(element: Element | null): number | null {
	if (!element) {
		return null;
	}

	const match = element.id.match(/field_\d+_(\d+)/);
	return match ? parseInt(match[1], 10) : null;
}

/**
 * Internal hook to sync quantity field value to store
 */
function useQuantityFieldSync(formId: number, bookingFieldId: number) {
	const quantityFieldId = useBookingStore((state) => state.quantityFieldId);
	const setQuantityFieldId = useBookingStore(
		(state) => state.setQuantityFieldId
	);
	const setQuantity = useBookingStore((state) => state.setQuantity);

	// Discover quantity field ID
	useEffect(() => {
		if (formId === 0) {
			return;
		}

		const form = document.querySelector(`#gform_${formId}`);
		if (!form) {
			return;
		}

		const quantityField = form.querySelector(
			`.gfield_quantity_${formId}_${bookingFieldId}`
		);
		const fieldId = extractFieldId(quantityField);
		if (fieldId) {
			setQuantityFieldId(fieldId);
		}
	}, [formId, bookingFieldId, setQuantityFieldId]);

	// Watch quantity field value
	const quantityValues = useFieldValues({
		formId,
		fieldIds: quantityFieldId ? [quantityFieldId] : [],
		shouldFetch: formId !== 0 && quantityFieldId !== null,
		transform: (value) => {
			const parsed = parseInt(value, 10);
			return isNaN(parsed) || parsed <= 0 ? 1 : parsed;
		},
	});

	// Sync quantity to store
	useEffect(() => {
		const quantity = quantityValues[0] || 1;
		setQuantity(quantity);
	}, [quantityValues, setQuantity]);
}

/**
 * Internal hook to sync occupancy field value to store
 */
function useOccupancyFieldSync(formId: number, bookingFieldId: number) {
	const occupancyFieldId = useBookingStore((state) => state.occupancyFieldId);
	const setOccupancyFieldId = useBookingStore(
		(state) => state.setOccupancyFieldId
	);
	const setOccupancy = useBookingStore((state) => state.setOccupancy);

	// Discover occupancy field ID
	useEffect(() => {
		if (formId === 0) {
			return;
		}

		const form = document.querySelector(`#gform_${formId}`);
		if (!form) {
			return;
		}

		const container = form.querySelector(
			`.ginput_container_occupancy[data-booking-field="${bookingFieldId}"], .ginput_container_gpb_occupancy[data-booking-field="${bookingFieldId}"]`
		);
		const fieldWrapper = container?.closest('[id^="field_"]') || null;
		const fieldId = extractFieldId(fieldWrapper);
		if (fieldId) {
			setOccupancyFieldId(fieldId);
		}
	}, [formId, bookingFieldId, setOccupancyFieldId]);

	// Watch occupancy field value
	const occupancyValues = useFieldValues({
		formId,
		fieldIds: occupancyFieldId ? [occupancyFieldId] : [],
		shouldFetch: formId !== 0 && occupancyFieldId !== null,
		transform: (value) => {
			if (!value || value === '') {
				return null;
			}
			const parsed = parseInt(value, 10);
			return isNaN(parsed) || parsed <= 0 ? null : parsed;
		},
	});

	// Sync occupancy to store
	useEffect(() => {
		const occupancy = occupancyValues[0] || null;
		setOccupancy(occupancy);
	}, [occupancyValues, setOccupancy]);
}

/**
 * Centralized hook to sync all DOM field values
 * not controlled by React to the store.
 *
 * All DOM watching happens here, and values are synced to the store.
 */
export function useDOMFieldSync({
	formId,
	bookingFieldId,
	serviceFieldId,
	fallbackServiceId,
	resourceFieldIds,
}: UseDOMFieldSyncOptions) {
	useServiceFieldSync(formId, serviceFieldId, fallbackServiceId);
	useResourceFieldSync(formId, resourceFieldIds);
	useQuantityFieldSync(formId, bookingFieldId);
	useOccupancyFieldSync(formId, bookingFieldId);
}
